import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { OFFERS, WHATSAPP_DEFAULT_MESSAGE } from "@/config/siteConfig";
import { waLink, trackEvent } from "@/lib/whatsapp";
import { Reveal } from "@/components/Reveal";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OFFER_OPTIONS = [...OFFERS.map((o) => `${o.name} (${o.data})`), "Internet", "Téléphonie", "Télévision", "Autre / Je ne sais pas encore"];

const initialForm = { name: "", phone: "", email: "", offer: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      trackEvent("soumission_formulaire", { offre: form.offer });
      toast.success("Demande envoyée !", {
        description: "Notre équipe vous contactera rapidement. Pour une réponse encore plus vite, écrivez-nous sur WhatsApp.",
      });
      setForm(initialForm);
    } catch {
      toast.error("Une erreur est survenue", {
        description: "Réessayez ou contactez-nous directement sur WhatsApp.",
      });
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "h-13 min-h-[52px] w-full rounded-xl border border-gray-200 bg-gray-50 px-4 text-base text-black outline-none transition-colors duration-300 focus:border-jeem-yellow focus:bg-white focus:ring-2 focus:ring-jeem-yellow/40";

  return (
    <section id="contact" className="border-t border-black/5 bg-white py-24 md:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-2xl px-4 md:px-8">
      <Reveal>
            <p className="mb-3 font-display text-xs font-bold uppercase tracking-[0.3em] text-gray-400">07 — Contact</p>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-black sm:text-4xl">
              Envoyez-nous votre demande
            </h2>
            <p className="mt-4 text-base text-gray-600">
              Remplissez ce court formulaire et notre équipe vous contactera. Pour une réponse immédiate, privilégiez WhatsApp.
            </p>

            <form onSubmit={submit} className="mt-9 space-y-4" data-testid="contact-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-sm font-semibold text-gray-800">Nom</label>
                  <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Votre nom" autoComplete="name" />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="mb-1.5 block text-sm font-semibold text-gray-800">Numéro de téléphone</label>
                  <input id="contact-phone" data-testid="contact-phone-input" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="514 000 0000" autoComplete="tel" />
                </div>
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-1.5 block text-sm font-semibold text-gray-800">Adresse courriel</label>
                <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="vous@exemple.com" autoComplete="email" />
              </div>
              <div>
                <label htmlFor="contact-offer" className="mb-1.5 block text-sm font-semibold text-gray-800">Offre qui vous intéresse</label>
                <select id="contact-offer" data-testid="contact-offer-select" required value={form.offer} onChange={set("offer")} className={inputCls}>
                  <option value="" disabled>Choisissez une offre</option>
                  {OFFER_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="mb-1.5 block text-sm font-semibold text-gray-800">Message</label>
                <textarea id="contact-message" data-testid="contact-message-input" required rows={4} value={form.message} onChange={set("message")} className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-base text-black outline-none transition-colors duration-300 focus:border-jeem-yellow focus:bg-white focus:ring-2 focus:ring-jeem-yellow/40" placeholder="Décrivez brièvement votre besoin..." />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="flex h-14 w-full items-center justify-center gap-2.5 rounded-full bg-black text-base font-bold text-white transition-colors duration-300 hover:bg-gray-800 disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                {sending ? "Envoi en cours..." : "Envoyer ma demande"}
              </button>
            </form>

            <a
              href={waLink(WHATSAPP_DEFAULT_MESSAGE)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("clic_whatsapp", { source: "contact" })}
              data-testid="contact-whatsapp-cta"
              className="mt-5 flex h-14 w-full items-center justify-center gap-2.5 rounded-full bg-jeem-yellow text-base font-bold text-black transition-colors duration-300 hover:bg-jeem-yellow-dark"
            >
              <WhatsAppIcon className="h-5 w-5" />
              Réponse immédiate sur WhatsApp
            </a>
          </Reveal>
      </div>
    </section>
  );
};
